import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { PermissionsService } from '../../../shared/services/permissions.service';
import { ResourceDTO } from '../../../shared/types/permission.types';
import { ResourceFormComponent } from '../resource-form/resource-form.component';

@Component({
  selector: 'app-resource-edit',
  imports: [
    CommonModule,
    RouterModule,
    ResourceFormComponent,
    PageHeaderComponent,
    DataStateComponent,
  ],
  templateUrl: './resource-edit.component.html',
})
export class ResourceEditComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly permissionsService = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  readonly resource = signal<ResourceDTO | null>(null);
  readonly loading = signal(true);
  readonly loadError = signal<string | null>(null);
  readonly submitting = signal(false);
  readonly serverError = signal<string | null>(null);

  private id = '';

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.id) {
      this.loadError.set('Identifiant de resource invalide.');
      this.loading.set(false);
      return;
    }
    this.fetch();
  }

  fetch(): void {
    this.loading.set(true);
    this.loadError.set(null);
    this.permissionsService
      .getResource(this.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r) => { this.resource.set(r); this.loading.set(false); },
        error: (err: unknown) => {
          this.loading.set(false);
          this.loadError.set(
            toErrorMessage(err, 'Impossible de charger cette resource.'),
          );
        },
      });
  }

  onSave(payload: ResourceDTO): void {
    this.submitting.set(true);
    this.serverError.set(null);
    this.permissionsService.updateResource(this.id, payload).subscribe({
      next: () => {
        this.submitting.set(false);
        void this.router.navigate(['/permissions/resources', this.id]);
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        this.serverError.set(toErrorMessage(err, 'errors.updateFailed'));
      },
    });
  }

  onCancel(): void { void this.router.navigate(['/permissions/resources', this.id]); }
}
